import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Button, Divider, Drawer, IconButton, Link, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';

import useAuth from '../../../auth/useAuth';
import useCart from '../../../cart/useCart';
import { ROLES } from '../../../config';
import { formatMoney } from '../products/ProductCard';
import ProductImagePlaceholder from '../products/ProductImagePlaceholder';
import QuantityStepper from '../products/QuantityStepper';

// Panel del carrito: se abre desde la derecha al hacer clic en el icono del header, para
// editar cantidades o quitar productos sin salir de la pagina en la que se esta.
// "Continuar la compra" y "Ver carrito completo" si navegan, porque son un paso deliberado.
export default function CartDrawer({ open, onClose }) {
  const { items, updateQuantity, removeItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  // El distribuidor tiene su propio catalogo, distinto del catalogo publico.
  const catalogPath = user?.role === ROLES.DISTRIBUIDOR ? '/distribuidor' : '/catalogo';

  const total = items.reduce((sum, item) => sum + item.product.price_pen * item.quantity, 0);

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 400 } } } }}>
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="h6">Tu carrito</Typography>
          <IconButton onClick={onClose} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />

        {items.length === 0 ? (
          <Stack spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', flexGrow: 1, p: 4, textAlign: 'center' }}>
            <ShoppingCartOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography color="text.secondary">Tu carrito está vacío.</Typography>
            <Button variant="contained" component={RouterLink} to={catalogPath} onClick={onClose}>
              Ir al catálogo
            </Button>
          </Stack>
        ) : (
          <>
            <Stack spacing={2} divider={<Divider />} sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
              {items.map((item) => (
                <Stack sx={{ alignItems: 'flex-start' }} key={item.product.id} direction="row" spacing={1.5}>
                  <Box sx={{ width: 56, height: 56, flexShrink: 0, borderRadius: 1, overflow: 'hidden' }}>
                    {item.product.image_url ? (
                      <Box
                        component="img"
                        src={item.product.image_url}
                        alt={item.product.name}
                        sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', bgcolor: 'background.paper' }}
                      />
                    ) : (
                      <ProductImagePlaceholder height="100%" iconFontSize={20} />
                    )}
                  </Box>
                  <Stack spacing={0.5} sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={item.product.name}>
                      {item.product.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatMoney(item.product.price_pen)} c/u
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mt: 0.5 }}>
                      <QuantityStepper
                        value={item.quantity}
                        onChange={(quantity) => updateQuantity(item.product.id, quantity)}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatMoney(item.product.price_pen * item.quantity)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <IconButton size="small" onClick={() => removeItem(item.product.id)} aria-label="Quitar">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>

            <Divider />
            <Stack spacing={1.5} sx={{ p: 2 }}>
              <Stack sx={{ justifyContent: 'space-between' }} direction="row">
                <Typography variant="subtitle1">Total</Typography>
                <Typography variant="subtitle1">{formatMoney(total)}</Typography>
              </Stack>
              <Button variant="contained" size="large" onClick={handleCheckout}>
                Continuar la compra
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/carrito" variant="body2" onClick={onClose}>
                  Ver carrito completo
                </Link>
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </Drawer>
  );
}

CartDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
